<script setup lang="ts">
import { VisBulletLegend } from "@unovis/vue";
import { eventBus } from "#bb/lib/eventBus";
import { computed, inject } from "vue";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Legend editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this bullet legend",
        action: async () => {
          // @ts-ignore - dynamic event name not in mitt typed Events
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Bullet Legend",
    icon: "lucide:book-marked",
  },
});

// consume the same provided report data with lifecycle flags
type InjectedReport = { data: any[]; loading?: boolean; initialized?: boolean };
const report = inject<InjectedReport>("report", {
  data: [],
  loading: false,
  initialized: false,
});
const chartData = computed(() =>
  report.loading || !report.initialized ? [] : report.data
);

// Compute the series keys either from settings or auto-detect
const seriesKeys = computed(() => {
  const configSeries = definitionData.value?.series;
  if (Array.isArray(configSeries) && configSeries.length > 0) {
    return configSeries;
  }
  const firstRow = chartData.value[0];
  if (firstRow && typeof firstRow === "object") {
    return Object.keys(firstRow);
  }
  return [];
});

// Identify label/identifier fields vs data fields
const labelFields = ["key", "name", "label", "id"];
const dataKeys = computed(() => {
  return seriesKeys.value.filter(key => !labelFields.includes(key.toLowerCase()));
});

const orientation = computed(
  () => definitionData.value?.orientation ?? "horizontal"
);

const colorAccessorCode = computed(
  () => definitionData.value?.colorAccessor ?? "(d, i) => null"
);

const color = computed(() => {
  try {
    return eval(colorAccessorCode.value);
  } catch (err) {
    console.error("Error evaluating color accessor:", err);
    return (d: any, i: number) => null;
  }
});

// Detect if this is single-value data (like donut) or multi-series data (like XY charts)
const isSingleValueData = computed(() => {
  // If there's only one data field (excluding label fields), it's single-value
  // In this case, each row represents a separate legend item
  return dataKeys.value.length === 1 && chartData.value.length > 1;
});

// Robust value accessor for extracting numeric values from data items
const valueAccessor = (d: any): number => {
  if (d == null) return 0;
  if (typeof d === "number") return d;
  if (typeof d === "object") {
    const v = (d as any).value ?? (d as any).score ?? (d as any).amount ?? 0;
    return typeof v === "number" ? v : Number(v) || 0;
  }
  const n = Number(d);
  return isNaN(n) ? 0 : n;
};

// Calculate total value for percentage calculations
const totalValue = computed(() => {
  if (!isSingleValueData.value) return 0;
  return chartData.value.reduce((acc, d) => acc + valueAccessor(d), 0);
});

// Format percentage
const formatPercent = (value: number, total: number): string => {
  if (!total) return "0%";
  return `${Math.round((value / total) * 100)}%`;
};

// Check if percentages should be shown (configurable option)
const showPercentages = computed(
  () => definitionData.value?.showPercentages ?? true
);

// Generate legend items based on data structure
const legendItems = computed(() => {
  const c = color.value;
  
  if (isSingleValueData.value) {
    // For donut/pie charts: each data row is a legend item
    const total = totalValue.value;
    
    return chartData.value.map((d: any, i: number) => {
      const label = typeof d === "object" 
        ? (d.key ?? d.name ?? d.label ?? d.id ?? String(i + 1))
        : String(d);
      const itemColor = c ? c(d, i) : undefined;
      
      // Calculate percentage if enabled
      let displayName = String(label);
      if (showPercentages.value) {
        const value = valueAccessor(d);
        const percent = formatPercent(value, total);
        displayName = `${label} ${percent}`;
      }
      
      const item: any = {
        name: displayName,
      };
      
      // Only include color if it's defined - let Unovis handle default colors
      if (itemColor !== null && itemColor !== undefined) {
        item.color = itemColor;
      }
      
      return item;
    });
  } else {
    // For XY charts: each data key is a legend item
    const keys = dataKeys.value;
    if (keys.length === 0) return [];
    
    return keys.map((key, i) => {
      // Create a mock data point for color evaluation
      const mockData = { [key]: 1 };
      const itemColor = c ? c(mockData, i) : undefined;
      
      const item: any = {
        name: key.charAt(0).toUpperCase() + key.slice(1),
      };
      
      // Only include color if it's defined - let Unovis handle default colors
      if (itemColor !== null && itemColor !== undefined) {
        item.color = itemColor;
      }
      
      return item;
    });
  }
});
</script>

<template>
  <div>
    <VisBulletLegend
      :orientation="orientation"
      :items="legendItems"
    />
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  editor: () => import("#bb/components/editor/unovis/legend/bullet.vue"),
  componentId: ComponentDefinitionIds.UNOVIS_LEGEND_BULLET,
  id: "bulletlegend",
  icon: "lucide:book-marked",
  type: "Bullet Legend",
  description:
    "Bullet Legend is a Unovis component for displaying series labels and colors.",
  default: {
    columnsSettings: { default: 1 },
    series: [],
    orientation: "horizontal",
    colorAccessor: "(d, i) => null",
    showPercentages: true,
  },
  categories: ["UNOVIS"],
  order: 12005,
};

</script>
